import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@host/components/NewUI/ComponentsLibrary';

import { onboardColors } from '../../theme/onboardTokens';
import { font, palette } from '../styles';
import { fitWithin, inkBounds } from './signatureGeometry';

// MUI's ArrowDropDown path as a data URI (the portal CSP allows data: images), in the
// host select's #AAAAAA arrow colour.
const SELECT_ARROW = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='${onboardColors.neutral.textDisabled}' d='M7 10l5 5 5-5z'/></svg>`
)}")`;

type Mode = 'type' | 'draw' | 'upload';

/* Cursive fonts mirror the approver signing widget (onboard_approval layout).
   Loaded via the candidate_portal layout <link>. */
const FONTS = ['Herr Von Muellerhoff', 'Mr Dafoe'];
const SIZES = ['30px', '35px', '40px', '45px', '50px', '55px', '60px'];

/* Preview canvas is the source of truth for the emitted PNG (same as approver). */
const PREVIEW_W = 540;
const PREVIEW_H = 120;
const DRAW_H = 140;
const STROKE_W = 2.5;
/* Blank margin kept around the drawn ink, in CSS px. */
const INK_PADDING = 8;
/* Largest enlargement of a small drawing, in PNG px per CSS px drawn. */
const MAX_INK_SCALE = 2;

interface Props {
  /* Called with the current signature as a PNG data-URL, or null when empty. */
  onChange: (value: string | null) => void;
}

const TABS: { mode: Mode; label: string }[] = [
  { mode: 'type', label: 'Type' },
  { mode: 'draw', label: 'Draw' },
  { mode: 'upload', label: 'Upload' }
];

const SignaturePad: React.FC<Props> = ({ onChange }) => {
  const [mode, setMode] = useState<Mode>('type');
  const tabRowRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const reduceMotion = useMemo(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    []
  );

  // The panels differ in height (Type, Draw, Upload), so animate the container to
  // whatever the visible panel measures.
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState<number | null>(null);
  const [panelAnimating, setPanelAnimating] = useState(false);
  const panelHeightRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;
    const measure = () => {
      const next = panel.offsetHeight;
      const previous = panelHeightRef.current;
      if (previous === next) return;
      panelHeightRef.current = next;
      if (previous != null && !reduceMotion) setPanelAnimating(true);
      setPanelHeight(next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [reduceMotion]);

  // Slide the underline to the active tab; re-measure when the row resizes
  // (fonts loading, the bolder active label, a narrower card).
  useLayoutEffect(() => {
    const row = tabRowRef.current;
    if (!row) return undefined;
    const measure = () => {
      const active = row.querySelector<HTMLElement>(`[data-tab="${mode}"]`);
      if (active) setIndicator({ left: active.offsetLeft, width: active.offsetWidth });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [mode]);
  const [text, setText] = useState('');
  const [fontFamily, setFontFamily] = useState(FONTS[0]);
  const [fontSize, setFontSize] = useState(SIZES[2]);
  const [hasInk, setHasInk] = useState(false);

  const previewRef = useRef<HTMLCanvasElement>(null);
  const drawRef = useRef<HTMLCanvasElement>(null);
  const uploadedRef = useRef<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const drawingRef = useRef(false);
  /* Snapshots of the draw canvas taken before each stroke — enables Undo. */
  const snapshotsRef = useRef<ImageData[]>([]);

  const renderPreview = useCallback(() => {
    const canvas = previewRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, PREVIEW_W, PREVIEW_H);

    let empty = true;
    if (mode === 'type') {
      const value = text.trim();
      if (value) {
        ctx.font = `${fontSize} ${fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = palette.black;
        ctx.fillText(value, PREVIEW_W / 2, PREVIEW_H / 2);
        empty = false;
      }
    } else if (mode === 'draw') {
      const draw = drawRef.current;
      const drawCtx = draw?.getContext('2d');
      if (hasInk && draw && drawCtx) {
        /* Crop to the ink and fit it without stretching, so the signature keeps
           the proportions it was drawn with whatever the pad's width (UIUX-4516). */
        const pixelRatio = draw.height / DRAW_H;
        const { data } = drawCtx.getImageData(0, 0, draw.width, draw.height);
        const ink = inkBounds(data, draw.width, draw.height, INK_PADDING * pixelRatio) ?? {
          x: 0,
          y: 0,
          width: draw.width,
          height: draw.height
        };
        const fit = fitWithin(
          ink.width,
          ink.height,
          PREVIEW_W,
          PREVIEW_H,
          MAX_INK_SCALE / pixelRatio
        );
        ctx.fillStyle = onboardColors.surface.white;
        ctx.fillRect(0, 0, PREVIEW_W, PREVIEW_H);
        ctx.drawImage(
          draw,
          ink.x,
          ink.y,
          ink.width,
          ink.height,
          fit.x,
          fit.y,
          fit.width,
          fit.height
        );
        empty = false;
      }
    } else if (mode === 'upload' && uploadedRef.current) {
      const img = uploadedRef.current;
      const { x, y, width, height } = fitWithin(img.width, img.height, PREVIEW_W, PREVIEW_H);
      ctx.drawImage(img, x, y, width, height);
      empty = false;
    }

    onChange(empty ? null : canvas.toDataURL('image/png'));
  }, [mode, text, fontFamily, fontSize, hasInk, onChange]);

  /* Re-render preview whenever any type/mode input changes. Draw and upload
     interactions call renderPreview directly after mutating their canvas/image. */
  useEffect(() => {
    renderPreview();
  }, [renderPreview]);

  /* Lets the resize handler repaint the preview without re-subscribing the
     observer every time the preview inputs change. */
  const renderPreviewRef = useRef(renderPreview);
  useEffect(() => {
    renderPreviewRef.current = renderPreview;
  }, [renderPreview]);

  /* Size the draw bitmap to its displayed box at the device pixel ratio, so
     strokes are neither stretched nor blurred. The canvas is display:none
     outside Draw mode, so this also runs when the tab is first opened. */
  const sizeDrawCanvas = useCallback(() => {
    const canvas = drawRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = Math.round(canvas.getBoundingClientRect().width * ratio);
    const height = Math.round(DRAW_H * ratio);
    if (width < 1 || (width === canvas.width && height === canvas.height)) return;

    /* Resizing wipes the bitmap, so carry the drawing across: anchored top-left
       at the same size, and shrunk uniformly only when it no longer fits (a
       phone rotated to portrait). */
    const previous = document.createElement('canvas');
    previous.width = canvas.width;
    previous.height = canvas.height;
    previous.getContext('2d')?.drawImage(canvas, 0, 0);

    canvas.width = width;
    canvas.height = height;
    /* Prime with a white background so the PNG isn't transparent. */
    ctx.lineWidth = STROKE_W * ratio;
    ctx.lineCap = 'round';
    ctx.strokeStyle = palette.black;
    ctx.fillStyle = onboardColors.surface.white;
    ctx.fillRect(0, 0, width, height);
    const scale = Math.min(width / previous.width, height / previous.height);
    ctx.drawImage(previous, 0, 0, previous.width * scale, previous.height * scale);
    /* Undo snapshots hold the old bitmap size and can't be replayed. */
    snapshotsRef.current = [];
    renderPreviewRef.current();
  }, []);

  useEffect(() => {
    const canvas = drawRef.current;
    if (!canvas) return undefined;
    const observer = new ResizeObserver(() => sizeDrawCanvas());
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [sizeDrawCanvas]);

  /* Nudge the browser to load the cursive fonts, then repaint the preview. */
  useEffect(() => {
    const fontSet = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!fontSet?.load) return;
    Promise.all(FONTS.map(async (f) => fontSet.load(`30px '${f}'`)))
      .then(() => renderPreview())
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pointOf = (e: React.PointerEvent<HTMLCanvasElement>, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) * canvas.width) / rect.width,
      y: ((e.clientY - rect.top) * canvas.height) / rect.height
    };
  };

  const beginStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    /* ResizeObserver only reports on a rendered frame, which a background tab
       can postpone; size synchronously so a stroke never lands on a stale bitmap. */
    sizeDrawCanvas();
    const canvas = drawRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    snapshotsRef.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const { x, y } = pointOf(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const continueStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const canvas = drawRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const { x, y } = pointOf(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };

  const endStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawRef.current?.releasePointerCapture(e.pointerId);
    drawingRef.current = false;
    renderPreview();
  };

  const clearDraw = () => {
    const canvas = drawRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.fillStyle = onboardColors.surface.white;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    snapshotsRef.current = [];
    setHasInk(false);
  };

  const undoDraw = () => {
    const canvas = drawRef.current;
    const ctx = canvas?.getContext('2d');
    const snapshot = snapshotsRef.current.pop();
    if (!canvas || !ctx || !snapshot) return;
    ctx.putImageData(snapshot, 0, 0);
    /* Check the pixels rather than the stack: ink carried across a resize has
       no snapshot of its own. */
    if (inkBounds(snapshot.data, snapshot.width, snapshot.height)) renderPreview();
    else setHasInk(false);
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file?.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const img = new Image();
      img.onload = () => {
        uploadedRef.current = img;
        renderPreview();
      };
      img.src = String(evt.target?.result ?? '');
    };
    reader.readAsDataURL(file);
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    background: 'none',
    border: 'none',
    padding: '0 16px',
    height: 32,
    fontFamily: font,
    fontSize: 16,
    fontWeight: active ? 600 : 400,
    color: active ? palette.primary : onboardColors.neutral.textSecondary,
    cursor: 'pointer',
    // Always transparent: the active line is the sliding indicator below.
    borderBottom: '3px solid transparent'
  });

  const selectStyle: React.CSSProperties = {
    /* The browser's own select chrome (heavy arrow, 38px tall) doesn't match the
       portal's inputs, so draw the Scout select's light grey arrow instead. */
    appearance: 'none',
    WebkitAppearance: 'none',
    padding: '8px 40px 8px 16px',
    lineHeight: '18px',
    border: `1px solid ${palette.divider}`,
    borderRadius: 6,
    fontFamily: font,
    fontSize: 14,
    backgroundColor: onboardColors.surface.white,
    backgroundImage: SELECT_ARROW,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '20px',
    color: palette.black,
    cursor: 'pointer',
    width: '100%',
    /* The portal layout has no global border-box reset, so width:100% +
       padding/border would overflow the parent card (UIUX-3780). */
    boxSizing: 'border-box'
  };

  const actionBtnStyle: React.CSSProperties = {
    background: 'none',
    border: `1px solid ${onboardColors.border.input}`,
    borderRadius: 4,
    padding: '5px 16px',
    fontFamily: font,
    fontSize: 13,
    color: onboardColors.neutral.textDark,
    cursor: 'pointer'
  };

  return (
    <div>
      <div
        ref={tabRowRef}
        style={{
          display: 'flex',
          position: 'relative',
          borderBottom: `0.75px solid ${onboardColors.border.hairline}`
        }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.mode}
            data-tab={tab.mode}
            onClick={() => setMode(tab.mode)}
            style={tabStyle(mode === tab.mode)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            bottom: 0,
            left: indicator.left,
            width: indicator.width,
            height: 3,
            background: palette.teal,
            transition: reduceMotion ? 'none' : 'left 250ms ease, width 250ms ease'
          }}
        />
      </div>

      <div
        onTransitionEnd={(e) => {
          // Children (buttons, inputs) have their own transitions that bubble up here.
          if (e.target === e.currentTarget && e.propertyName === 'height') {
            setPanelAnimating(false);
          }
        }}
        style={{
          height: panelHeight ?? 'auto',
          // Clip only while the height animates so focus rings aren't cut off at rest.
          overflow: panelAnimating ? 'hidden' : 'visible',
          transition: reduceMotion ? 'none' : 'height 250ms ease'
        }}
      >
        <div ref={panelRef}>
          {mode === 'type' ? (
            <div style={{ padding: '12px 0 0' }}>
              <input
                onChange={(e) => setText(e.target.value)}
                placeholder="Please type your name"
                type="text"
                value={text}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '8px 16px',
                  border: `1px solid ${palette.divider}`,
                  borderRadius: 6,
                  fontFamily: font,
                  fontSize: 14,
                  color: palette.black,
                  outline: 'none'
                }}
              />
              <div style={{ display: 'flex', gap: 14, marginTop: 8 }}>
                <label
                  htmlFor="signature-font"
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    fontSize: 14,
                    color: onboardColors.neutral.textPlaceholderDark
                  }}
                >
                  Font
                  <select
                    id="signature-font"
                    onChange={(e) => setFontFamily(e.target.value)}
                    style={selectStyle}
                    value={fontFamily}
                  >
                    {FONTS.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </label>
                <label
                  htmlFor="signature-font-size"
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    fontSize: 14,
                    color: onboardColors.neutral.textPlaceholderDark
                  }}
                >
                  Font size
                  <select
                    id="signature-font-size"
                    onChange={(e) => setFontSize(e.target.value)}
                    style={selectStyle}
                    value={fontSize}
                  >
                    {SIZES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          ) : null}

          <div style={{ padding: '12px 0 0', display: mode === 'draw' ? 'block' : 'none' }}>
            <div
              style={{
                border: `1px solid ${palette.divider}`,
                borderRadius: 6,
                overflow: 'hidden',
                background: onboardColors.surface.white,
                position: 'relative'
              }}
            >
              {!hasInk ? (
                <span
                  style={{
                    position: 'absolute',
                    top: 8,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    fontSize: 12,
                    color: onboardColors.neutral.disabled,
                    pointerEvents: 'none',
                    userSelect: 'none'
                  }}
                >
                  Use mouse or touchpad to draw
                </span>
              ) : null}
              <canvas
                ref={drawRef}
                data-testid="signature-draw"
                height={DRAW_H}
                onPointerDown={beginStroke}
                onPointerLeave={endStroke}
                onPointerMove={continueStroke}
                onPointerUp={endStroke}
                width={PREVIEW_W}
                style={{
                  display: 'block',
                  width: '100%',
                  height: DRAW_H,
                  touchAction: 'none',
                  cursor: 'crosshair'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
              <button onClick={clearDraw} style={actionBtnStyle} type="button">
                Clear
              </button>
              <button onClick={undoDraw} style={actionBtnStyle} type="button">
                Undo
              </button>
            </div>
          </div>

          {mode === 'upload' ? (
            <div style={{ padding: '12px 0 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Button
                  id="signature-browse-button"
                  onClick={() => fileInputRef.current?.click()}
                  size="small"
                >
                  Browse
                </Button>
                <input
                  ref={fileInputRef}
                  accept="image/*"
                  aria-label="Signature image file"
                  id="signature-file"
                  onChange={onFile}
                  style={{ display: 'none' }}
                  type="file"
                />
                <span
                  style={{
                    fontSize: 14,
                    color: onboardColors.neutral.textMutedAlt,
                    background: palette.pillNeutral,
                    padding: '8px 16px',
                    borderRadius: 4,
                    flex: 1
                  }}
                >
                  Select a signature image file (JPG, PNG, GIF)
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <p
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: '#a6a9aa',
          textAlign: 'center',
          margin: '12px 0 6px'
        }}
      >
        Please confirm signature is correct before proceeding
      </p>
      <canvas
        ref={previewRef}
        data-testid="signature-preview"
        height={PREVIEW_H}
        width={PREVIEW_W}
        style={{
          display: 'block',
          width: '100%',
          boxSizing: 'border-box',
          height: 176,
          /* Letterbox the fixed-size PNG instead of stretching it to the box. */
          objectFit: 'contain',
          border: `1px dashed ${palette.inputBorder}`,
          borderRadius: 6,
          background: onboardColors.surface.white
        }}
      />
    </div>
  );
};

export default SignaturePad;
